import React, { Suspense, useState, useEffect } from "react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import "./App.css";
import { ToastContainer } from "react-toastify";
import { RouterProvider } from "raviger";
import AuthenticatedContent from "./containers/AuthenticatedContent";
import NoInternet from "./components/shared/NoInternet";
import { isNetworkError } from "./backend/request";
import { watchCrossTabAuthChanges } from "./backend/auth";

export function getBasePath() {
    return window.location.origin;
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error: any) => {
                if (error && error.response && error.response.status === 401) return false;
                if (isNetworkError(error)) return false;
                return failureCount < 3;
            }
        },
        mutations: {
            retry: (failureCount, error: any) => {
                if (error && error.response && error.response.status === 401) return false;
                if (isNetworkError(error)) return false;
                return failureCount < 2;
            }
        }
    }
});

function App() {
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // If the session changes in another tab (logout + login as a different/impersonated
    // user, or an admin ending an impersonation session), resync this tab instead of letting
    // it keep running with a stale role against a token that's since moved on — see auth.ts.
    useEffect(() => watchCrossTabAuthChanges(), []);

    if (!isOnline) {
        return <NoInternet />;
    }
    return (
        <QueryClientProvider client={queryClient}>
            <Suspense fallback="loading...">
                <RouterProvider >
                    <AuthenticatedContent />
                </RouterProvider>
                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={true}
                    newestOnTop={true}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                />
            </Suspense>
        </QueryClientProvider>
    )
}
export default App;
